/**
 * Session provenance -- "was this session obtained WITH credentials?"
 *
 * WHAT THIS IS, AND WHY IT IS NOT A SECOND ANONYMOUS MARKER.
 *
 * `anonymousStorefrontSession.js` records a CLIENT-SIDE OBSERVATION: "this
 * gate auto-provisioned the session it is looking at". This module records a
 * SERVER-STATED FACT: `Token.is_anonymous_storefront`, reported by every auth
 * door as `session_is_anonymous_storefront`. The first is an inference drawn
 * at one moment by one component; the second is the authority that moment was
 * trying to approximate.
 *
 * They agree in the ordinary case, which is exactly why the distinction has to
 * be written down: the marker is a UI-visibility flag whose fail-safe is
 * "assume anonymous and hide the personal links", while this is a session
 * FACT whose fail-safe is the opposite (see below). Overloading one key with
 * both meanings would force one of the two fail-safes to be wrong.
 *
 * WHY IT EXISTS -- GH #3242.
 *
 * `StorefrontSessionGate` has to decide, before the storefront renders,
 * whether the browser already holds a valid session for this store or needs to
 * be swapped onto the store's shared shopper. It used to answer that by
 * comparing the held `userId` against the store's TEMPLATE shopper id. That
 * comparison is true for exactly ONE account and false for everyone else, so:
 *
 *   - a real signed-in shopper was swapped out of their own session and
 *     re-marked anonymous (the reported bug -- their account menu vanished);
 *   - an anonymous visitor, whose per-visitor id also never equals the
 *     template, would have been swapped on every page load, minting a fresh
 *     account and dropping their cart -- which is why a marker check had
 *     already been bolted on to rescue that one population.
 *
 * Both populations are asking the same question, and the server has always
 * known the answer. Reading it here replaces both arms with one fact.
 *
 * WHY localStorage. The gate runs on a COLD page load, where Vuex and Pinia
 * are both empty (they are memory-only) -- that is precisely the state in
 * which the bug fires. The answer therefore has to survive a reload for as
 * long as the token it describes does, and it has to be readable
 * SYNCHRONOUSLY, before any network call. That is localStorage, for the same
 * reasons the anonymous marker uses it.
 *
 * FAIL-SAFE DIRECTION -- OPPOSITE to the anonymous marker, deliberately.
 *
 * Unreadable storage, or an absent value, reports CREDENTIALED (false). The
 * marker fails toward "anonymous" because its question is "may I show this
 * person's saved addresses?", where the cautious answer is no. THIS module's
 * question is "may I destroy the session this browser is holding?", where the
 * cautious answer is also no -- but that is the false branch, not the true
 * one. Failing toward "anonymous" here would swap a real shopper out of their
 * own session on any storage hiccup, which is the bug this module exists to
 * fix.
 *
 * The two fail-safes are consistent, not contradictory: BOTH decline to take a
 * destructive action under uncertainty. They differ only in which boolean
 * spells "decline", which is why they cannot share a key.
 *
 * Note the safety asymmetry that makes this the correct default. A session
 * wrongly treated as credentialed is not swapped, and the storefront chrome
 * still withholds the personal links unless the ANONYMOUS MARKER also says
 * personal -- so the exposure that marker guards stays guarded either way.
 */

// THIS MODULE MUST STAY A LEAF (only `pinia`, a library). `@/axios` reaches it
// through router/sessionBearer.js while `@/axios` is still evaluating; an app
// import here can re-open the cycle that left `services/api` undefined
// (sandbox 8088, 2026-10-01; regression net __tests__/axios-import-cycle.spec.js).
import { getActivePinia } from "pinia";

export const SESSION_PROVENANCE_KEY = "sessionIsAnonymousStorefront";

/**
 * Record what the server said about the session just established.
 *
 * Call at every door that mints or re-reports a session: login, `/auth/me`
 * rehydration, and the anonymous `getToken` swap. `value` is the raw
 * `session_is_anonymous_storefront` field.
 *
 * A NON-BOOLEAN value (an older API that omits the field, `undefined`,
 * `null`) CLEARS the record rather than writing a guess. A stale value from
 * the PREVIOUS session would be worse than none: it would describe a session
 * that no longer exists, which is how the anonymous marker produced GH #2941
 * defect 2.
 */
export function recordSessionProvenance(value) {
    try {
        if (typeof value !== "boolean") {
            localStorage.removeItem(SESSION_PROVENANCE_KEY);
            return;
        }
        localStorage.setItem(SESSION_PROVENANCE_KEY, value ? "1" : "0");
    } catch {
        // Storage unavailable. isAnonymousStorefrontToken() reports
        // "credentialed" in that case, which declines to swap -- see the
        // FAIL-SAFE DIRECTION note above.
    }
}

/** Drop the record. Call when the session ends (logout). */
export function clearSessionProvenance() {
    try {
        localStorage.removeItem(SESSION_PROVENANCE_KEY);
    } catch {
        // See recordSessionProvenance.
    }
}

/**
 * True only when the server SAID this session is anonymous.
 *
 * Absent or unreadable => false (credentialed). See FAIL-SAFE DIRECTION.
 */
export function isAnonymousStorefrontToken() {
    try {
        return localStorage.getItem(SESSION_PROVENANCE_KEY) === "1";
    } catch {
        return false;
    }
}

export default isAnonymousStorefrontToken;

// ---------------------------------------------------------------------------
// THE "is this an anonymous storefront session?" predicate -- the ONE place
// that answers it for any decision about a CREDENTIAL (gate bar-raiser P1-3
// and security P1-1 on #3957). Callers: the request bearer itself
// (router/sessionBearer.js), the storefront session gate, the auth
// interceptor, and raiseError's 401 branch.
//
// Not to be confused with `isAnonymousStorefrontSession()` in
// router/anonymousStorefrontSession.js. That one answers a DIFFERENT question
// -- "may the storefront chrome show this session's personal links?" -- and
// deliberately fails toward "anonymous" (hide them). For a credential the safe
// direction is the opposite (treat it as a real user's: no memory hold, no
// re-mint, /login on expiry), so the two must not share a predicate.
// ---------------------------------------------------------------------------

// The id `stores/account.js` registers with `defineStore('account', ...)`.
// Read through Pinia's state registry rather than by importing the store,
// which would break the leaf rule above.
const ACCOUNT_STORE_ID = "account";

function cleanToken(token) {
    if (token == null) return null;
    const value = String(token).trim().replace(/^["']+|["']+$/g, "");
    return value.length > 0 ? value : null;
}

function readStorage(key) {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

/** The in-memory session (Pinia account state), or null. */
function heldSession() {
    const pinia = getActivePinia();
    const state = pinia && pinia.state && pinia.state.value;
    return (state && state[ACCOUNT_STORE_ID]) || null;
}

/**
 * The bearer the next request will carry, and where it came from.
 *
 * localStorage first (so a sign-in in another tab takes effect here, as it
 * always has). Only when storage has lost the key does the in-memory copy
 * stand in -- and only an ANONYMOUS one: a credentialed token missing from
 * storage usually means it was removed on purpose (a logout in another tab),
 * and logout does not revoke tokens server-side.
 *
 * @returns {{ bearer: (string|null), fromMemory: boolean }}
 */
export function resolveBearer() {
    const stored = cleanToken(readStorage("token"));
    if (stored) return { bearer: stored, fromMemory: false };
    const held = heldSession();
    const heldToken = held ? cleanToken(held.token) : null;
    if (heldToken && isAnonymousStorefrontSessionNow(heldToken)) {
        return { bearer: heldToken, fromMemory: true };
    }
    return { bearer: null, fromMemory: false };
}

/**
 * Is `bearer` (default: the bearer the next request will carry) an anonymous
 * storefront session?
 *
 * Positive evidence ABOUT THAT BEARER, or no:
 *   - the in-memory flag, when the in-memory token IS this bearer. A tab that
 *     browsed anonymously keeps its flag after another tab signs a real user
 *     in; the flag then describes a token that is no longer the one sent;
 *   - otherwise the server-stated provenance recorded alongside the STORED
 *     token. Every credentialed sign-in clears it (store/index.js `login`),
 *     logout clears it, and every hydration re-records it.
 * No bearer, or unreadable evidence, is "no".
 */
export function isAnonymousStorefrontSessionNow(bearer) {
    const subject =
        bearer === undefined ? resolveStoredOrNull() : cleanToken(bearer);
    if (!subject) return false;
    const held = heldSession();
    if (held && cleanToken(held.token) === subject) {
        return held.anonymousStorefront === true;
    }
    return subject === cleanToken(readStorage("token")) &&
        readStorage(SESSION_PROVENANCE_KEY) === "1";
}

// The default subject: what `resolveBearer` would send, without recursing
// through it (resolveBearer itself asks this predicate about the memory copy).
function resolveStoredOrNull() {
    const stored = cleanToken(readStorage("token"));
    if (stored) return stored;
    const held = heldSession();
    return held && held.anonymousStorefront ? cleanToken(held.token) : null;
}
